/**
 * Context types for AI scenario generation
 * These are used internally by services and are not validated at runtime
 */

/**
 * Availability slot for an employee
 */
export interface AvailabilitySlot {
  date: string; // ISO date
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  type: 'available' | 'preferred' | 'unavailable';
}

/**
 * Employee context for AI processing
 */
export interface EmployeeContext {
  id: string;
  name: string;
  skills: string[];
  availability: AvailabilitySlot[];
  hoursThisMonth: number;
  hourlyRate: number;
  /** Target weekly hours from employment period */
  targetHoursPerWeek?: number;
  /** Remaining hours available for the current planning week */
  remainingHoursForWeek?: number;
  /** Employment ratio (0-1), e.g. 0.5 for 50% part-time */
  employmentRatio?: number;
}

/**
 * Shift context for AI processing
 */
export interface ShiftContext {
  shiftUnitId: string;
  date: string; // ISO date
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  requiredSkills: string[];
  currentAssignment?: string; // employeeId if already assigned
}

/**
 * Plan context containing all data needed for scenario generation
 */
export interface PlanContext {
  planId: string;
  startDate: string; // ISO date
  endDate: string; // ISO date
  employees: EmployeeContext[];
  shifts: ShiftContext[];
}

/**
 * Progress event emitted during job processing
 */
export interface ProgressEvent {
  jobId: string;
  progress: number;
  message?: string;
  timestamp: number;
}

/**
 * Completion event emitted when job finishes
 */
export interface CompletionEvent {
  jobId: string;
  result: unknown;
  timestamp: number;
}

/**
 * Error event emitted when job fails
 */
export interface ErrorEvent {
  jobId: string;
  error: string;
  timestamp: number;
}
